const VERIFY_URLS = {
  turnstile: 'https://challenges.cloudflare.com/turnstile/v0/siteverify',
  hcaptcha: 'https://api.hcaptcha.com/siteverify'
} as const

const TIMEOUT_MS = 5000

export interface CaptchaConfig {
  provider: 'turnstile' | 'hcaptcha'
  secretKey: string
}

export interface CaptchaOptions {
  logger?: { warn: (obj: Record<string, unknown>, msg: string) => void }
}

/**
 * Verify a CAPTCHA token against the provider API.
 * Returns error message string on failure, null on success.
 * Fail-open on API/network errors (warns and returns null).
 * Skips verification in test environment.
 */
export async function verifyCaptcha(
  token: string | undefined,
  config: CaptchaConfig,
  options?: CaptchaOptions
): Promise<string | null> {
  // Skip in test environment
  if (process.env['NODE_ENV'] === 'test') return null

  if (!token) return 'CAPTCHA token required'

  const url = VERIFY_URLS[config.provider]
  const body = new URLSearchParams({
    secret: config.secretKey,
    response: token
  })

  try {
    const res = await fetch(url, {
      method: 'POST',
      body,
      signal: AbortSignal.timeout(TIMEOUT_MS)
    })
    const data = await res.json() as { success: boolean }

    if (!data.success) return 'CAPTCHA verification failed'
    return null
  } catch (err) {
    // API/network failure → fail-open
    options?.logger?.warn(
      { error: (err as Error).message },
      'CAPTCHA verification failed, allowing request (fail-open)'
    )
    return null
  }
}
